import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Tooltip,
} from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridRowId,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro'
import { useSelector } from '@state/store'
import {
  setItPage,
  setItLimit,
  setItSearch,
  setItTypeFilter,
  clearItFilters,
} from '@state/reducers/itEquipmentReducer'
import {
  findAllItEquipment,
  deleteItEquipment,
  deleteBulkItEquipment,
} from '@state/thunks/itEquipmentThunk'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { ItEquipmentForm } from './ItEquipmentForm'
import { EditIcon, Trash2 } from 'lucide-react'
import { ItEquipment } from '@services/api'
import { Add, Refresh, Delete } from '@mui/icons-material'
import { debounce } from '@mui/material'

function CustomToolbar({
  onAdd,
  onRefresh,
  onDelete,
  selectedCount,
}: {
  onAdd: () => void
  onRefresh: () => void
  onDelete: () => void
  selectedCount: number
}) {
  return (
    <GridToolbarContainer
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          IT Equipments
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {selectedCount > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={onDelete}
            size="small"
          >
            Delete ({selectedCount})
          </Button>
        )}
        <Button variant="outlined" startIcon={<Refresh />} onClick={onRefresh} size="small">
          Refresh
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={onAdd} size="small">
          Add Equipment
        </Button>
      </Box>
    </GridToolbarContainer>
  )
}

export default function ItEquipments() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const [itEquipment, setItEquipment] = useState<ItEquipment | undefined>(undefined)
  
  const [searchInput, setSearchInput] = useState('')

  const handleCloseAddDialog = useCallback(() => {
    setOpenAddDialog(false)
    setItEquipment(undefined)
  }, [])

  const { data, pagination, filters, loading } = useSelector((state) => state.itEquipment)

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(setItSearch(value))
      }, 500),
    [dispatch]
  )

  useEffect(() => {
    return () => {
      debouncedSearch.clear()
    }
  }, [debouncedSearch])

  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  const columns: GridColumns = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'label',
      headerName: 'Label',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'ip',
      headerName: 'IP Address',
      flex: 1,
      minWidth: 130,
    },
    {
      field: 'mac',
      headerName: 'MAC Address',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'port',
      headerName: 'Port',
      width: 100,
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.site?.label || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setItEquipment(params.row)
                setOpenAddDialog(true)
              }}
            >
              <EditIcon size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteSingle(params.row.id)}
            >
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  useEffect(() => {
    dispatch(
      findAllItEquipment({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type || undefined,
      }),
    )
  }, [dispatch, pagination.page, pagination.limit, filters.search, filters.type])

  const onRefresh = useCallback(() => {
    dispatch(
      findAllItEquipment({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type || undefined,
      }),
    )
  }, [dispatch, pagination.page, pagination.limit, filters.search, filters.type])

  const handleDeleteSingle = async (id: number) => {
    dispatch(deleteItEquipment(id))
    onRefresh()
  }

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection)
  }

  const _onDeleteConfirm = async () => {
    dispatch(deleteBulkItEquipment(selectedRows as number[]))
    setIsDeleteDialogOpen(false)
    setSelectedRows([])
    onRefresh()
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchInput(value)
    debouncedSearch(value)
  }

  const handleTypeFilterChange = (event: any) => {
    dispatch(setItTypeFilter(event.target.value))
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 130px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="Search (Code or Label)"
            variant="outlined"
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300 }}
            placeholder="Type to search..."
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filters.type} onChange={handleTypeFilterChange} label="Type">
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="SERVER">Server</MenuItem>
              <MenuItem value="WORKSTATION">Workstation</MenuItem>
              <MenuItem value="PRINTER">Printer</MenuItem>
              <MenuItem value="SCANNER">Scanner</MenuItem>
              <MenuItem value="NETWORK">Network Device</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => {
              setSearchInput('')
              debouncedSearch.clear()
              dispatch(clearItFilters())
            }}
            size="small"
          >
            Clear Filters
          </Button>

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Total: {pagination.total} equipment(s)
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={data}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableSelectionOnClick
          pagination
          paginationMode="server"
          page={pagination.page - 1}
          pageSize={pagination.limit}
          rowCount={pagination.total}
          onPageChange={(newPage) => dispatch(setItPage(newPage + 1))}
          onPageSizeChange={(newPageSize) => dispatch(setItLimit(newPageSize))}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onSelectionModelChange={_onSelectionChange}
          selectionModel={selectedRows}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              onAdd: () => {
                setItEquipment(undefined)
                setOpenAddDialog(true)
              },
              onRefresh,
              onDelete: () => {
                setIsDeleteDialogOpen(true)
              },
              selectedCount: selectedRows.length,
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="lg"
        fullWidth
        aria-labelledby="form-dialog-title"
        scroll="paper"
      >
        <DialogTitle>{itEquipment ? 'Edit' : 'Add'} IT Equipment</DialogTitle>
        <DialogContent>
          <ItEquipmentForm
            onClose={() => {
              handleCloseAddDialog()
              onRefresh()
            }}
            row={itEquipment}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description={`Voulez-vous vraiment supprimer ${selectedRows.length} équipement(s) IT sélectionné(s) ?`}
      />
    </Box>
  )
}